/* eslint-disable prefer-destructuring */
/* eslint-disable no-restricted-syntax */
import * as _ from "lodash";
import {
  BaseEntityEvent,
  BaseEntityListener,
  BaseModel,
  CanvasModel,
  CanvasModelGenerics,
  LayerModel,
  DeserializeEvent,
} from "@projectstorm/react-canvas-core";
import { LinkModel, NodeModel, NodeLayerModel, LinkLayerModel } from "@projectstorm/react-diagrams";

export interface DiagramListener extends BaseEntityListener {
  // @ts-ignore
  nodesUpdated?(event: BaseEntityEvent & { node: NodeModel; isCreated: boolean }): void;
  // @ts-ignore
  linksUpdated?(event: BaseEntityEvent & { link: LinkModel; isCreated: boolean }): void;
}

export interface DiagramModelGenerics extends CanvasModelGenerics {
  LISTENER: DiagramListener;
}

class DiagramModel<G extends DiagramModelGenerics = DiagramModelGenerics> extends CanvasModel<G> {
  protected activeNodeLayer: NodeLayerModel | undefined;

  protected activeLinkLayer: LinkLayerModel | undefined;

  protected activeCrossConnectLayer: LinkLayerModel | undefined;

  constructor(options: G["OPTIONS"] = {}) {
    super(options);
    // @ts-ignore
    this.addLayer(new LinkLayerModel(), false);
    // @ts-ignore
    this.addLayer(new NodeLayerModel());
    // @ts-ignore
    this.addLayer(new LinkLayerModel(), true);
  }

  deserialize(event: DeserializeEvent<this>) {
    this.layers = [];
    super.deserialize(event);
  }

  addLayer(layer: LayerModel, cc?: boolean): void {
    super.addLayer(layer);
    if (layer instanceof NodeLayerModel) {
      this.activeNodeLayer = layer;
    }
    if (layer instanceof LinkLayerModel && !cc) {
      this.activeLinkLayer = layer;
    }
    if (layer instanceof LinkLayerModel && cc) {
      this.activeCrossConnectLayer = layer;
    }
  }

  getLinkLayers(): LinkLayerModel[] {
    return _.filter(this.layers, (layer) => {
      return layer instanceof LinkLayerModel;
    }) as unknown as LinkLayerModel[];
  }

  getNodeLayers(): NodeLayerModel[] {
    return _.filter(this.layers, (layer) => {
      return layer instanceof NodeLayerModel;
    }) as unknown as NodeLayerModel[];
  }

  getActiveNodeLayer(): NodeLayerModel | undefined {
    return this.activeNodeLayer;
  }

  getActiveLinkLayer(): LinkLayerModel | undefined {
    return this.activeLinkLayer;
  }

  getActiveCCLayer(): LinkLayerModel | undefined {
    return this.activeCrossConnectLayer;
  }

  getNode(node: string): NodeModel | null {
    for (const layer of this.getNodeLayers()) {
      const model = layer.getModel(node);
      if (model) {
        return model;
      }
    }
    return null;
  }

  getLink(link: string): LinkModel | null {
    for (const layer of this.getLinkLayers()) {
      const model = layer.getModel(link);
      if (model) {
        return model;
      }
    }
    return null;
  }

  addAll(...models: BaseModel[]): BaseModel[] {
    _.forEach(models, (model) => {
      if (model instanceof LinkModel) {
        this.addLink(model);
      } else if (model instanceof NodeModel) {
        this.addNode(model);
      }
    });
    return models;
  }

  addLink(link: LinkModel): LinkModel {
    if (link.getOptions().type && link.getOptions().type !== "Bandwidth") {
      this.getActiveCCLayer()?.addModel(link);
    } else {
      this.getActiveLinkLayer()?.addModel(link);
    }
    this.fireEvent(
      {
        link,
        isCreated: true,
      },
      "linksUpdated"
    );
    return link;
  }

  addNode(node: NodeModel): NodeModel {
    this.getActiveNodeLayer()?.addModel(node);
    this.fireEvent({ node, isCreated: true }, "nodesUpdated");
    return node;
  }

  removeLink(link: LinkModel) {
    const removed = _.some(this.getLinkLayers(), (layer) => {
      return layer.removeModel(link);
    });
    if (removed) {
      this.fireEvent({ link, isCreated: false }, "linksUpdated");
    }
  }

  removeNode(node: NodeModel) {
    const removed = _.some(this.getNodeLayers(), (layer) => {
      return layer.removeModel(node);
    });
    if (removed) {
      this.fireEvent({ node, isCreated: false }, "nodesUpdated");
    }
  }

  getLinks(): LinkModel[] {
    return _.flatMap(this.getLinkLayers(), (layer) => {
      return _.values(layer.getModels());
    });
  }

  getNodes(): NodeModel[] {
    return _.flatMap(this.getNodeLayers(), (layer) => {
      return _.values(layer.getModels());
    });
  }
}

export default DiagramModel;
